import { zodResolver } from '@hookform/resolvers/zod'
import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { IFile } from '@repo/toolkit/types'
import Badge from '@repo/ui/badge'
import Box from '@repo/ui/box'
import { UploadIcon } from '@repo/ui/icons'
import Modal, { MODAL_CONTENT_CLASSNAME } from '@repo/ui/modal'
import Typography from '@repo/ui/typography'
import { useForm } from 'react-hook-form'

import { useModalManager } from '../../../lib/hooks/use-modal-manager'
import useTranslate from '../../../lib/hooks/use-translate'
import { HostFamilyInput } from '../../../lib/types/models/host-family/host-family-input'
import { BaseFormProps } from '../../../lib/types/utils'
import { getSubmitAction } from '../../../lib/utils/buttons-config/validation-buttons-config'
import { FORM_MODE } from '../../../utils/constants/form.constants'
import { Form, FormControl, FormField, FormItem } from '../../elements/forms/form'
import ControlledInput from '../../elements/inputs/controlled-input'
import MediaAttachmentList from '../../elements/media-attachment-list'
import ValidationButtons from '../../elements/validation-buttons'
import HostFamilyAccommodationDetailsForm from './host-family-accommodation-details-form'
import HostFamilyAddressForm from './host-family-address-form'
import HostFamilyChildrenTableForm from './host-family-children-table-form'
import HostFamilyFormHeader from './host-family-form-header'
import HostFamilyLocalRepresentativeForm from './host-family-local-representative-form'
import HostFamilyParentsInformationForm from './host-family-parents-information-form'
import HostFamilyRelativeTableForm from './host-family-relative-table-form'
import HostFamilyStudentAccommodationForm from './host-family-student-accommodation-form'
import HostFamilyStudentAppreciations from './host-family-student-appreciations'
import HostFamilyUploadFileForm from './host-family-upload-file-form'
import { hostFamilyFormSchema } from './utils'

const HostFamilyForm = ({
  defaultValues,
  showLocalRepresentative,
  hostFamilyId,
  ...validationButtonProps
}: BaseFormProps<HostFamilyInput> & {
  // only for host families of PARTNER OLs with OTO Individual enabled
  showLocalRepresentative?: boolean
  // only for an existing host family: shows the appreciations of the students it hosted
  hostFamilyId?: number
}) => {
  const { currentLanguage, t } = useTranslate()
  const form = useForm<HostFamilyInput>({
    resolver: zodResolver(hostFamilyFormSchema(currentLanguage, t)),
    defaultValues,
    mode: FORM_MODE
  })

  const files: IFile[] = form.watch('picturesAndVideos') || []

  const { modalState, toggleModal, closeModel } = useModalManager<'create', IFile>(['create'])
  return (
    <Form {...form}>
      <Box>
        <form
          onSubmit={form.handleSubmit(getSubmitAction(validationButtonProps.buttonsConfig))}
          className="flex flex-col gap-4"
        >
          {defaultValues && <HostFamilyFormHeader />}
          <div className="grid grid-cols-2 gap-3">
            <div className="flex flex-col gap-4">
              <Box className="bg-primary-light rounded-xl">
                <Typography variant="text" color={tailwindColors.primary.DEFAULT}>
                  Family
                </Typography>
                <div className="h-4" />
                <FormField
                  control={form.control}
                  name="familyName"
                  render={({ field }) => (
                    <FormItem className="flex-1">
                      <FormControl>
                        <ControlledInput
                          {...field}
                          required
                          type="text"
                          label="Hostess's last name"
                          inputClassName="bg-primary-light"
                          labelClassName="bg-primary-light"
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
              </Box>
              <HostFamilyParentsInformationForm />
              <HostFamilyAddressForm />
            </div>
            <div className="flex flex-col gap-4">
              <HostFamilyAccommodationDetailsForm />
              <MediaAttachmentList
                title="Pictures and videos"
                uploadLabel="Upload image/video"
                files={files}
                getLabel={(file) => file.name}
                getInfoSection={(file) => {
                  return <Badge variant="white" text={file.caption ?? '-'} />
                }}
                onClick={() => toggleModal('create')}
                onDelete={(file) => {
                  if (file) {
                    form.setValue(
                      'picturesAndVideos',
                      files.filter((f) => f.path !== file.path)
                    )
                  }
                }}
              />
              <HostFamilyStudentAccommodationForm />
              {showLocalRepresentative && <HostFamilyLocalRepresentativeForm />}
            </div>
          </div>
          <HostFamilyChildrenTableForm />
          <HostFamilyRelativeTableForm />
          {hostFamilyId !== undefined && (
            <HostFamilyStudentAppreciations id={hostFamilyId} isEnglish />
          )}

          <ValidationButtons {...validationButtonProps} />
        </form>
      </Box>

      <Modal
        isOpen={modalState.create}
        title={'Upload image/video'}
        icon={UploadIcon}
        hideActions
        size="medium"
        onCancel={() => closeModel('create')}
      >
        <HostFamilyUploadFileForm
          className={MODAL_CONTENT_CLASSNAME}
          onCancel={() => closeModel('create')}
          onConfirm={(values) => {
            form.setValue('picturesAndVideos', [
              ...(form.getValues('picturesAndVideos') || []),
              values.file
            ])
            closeModel('create')
          }}
        />
      </Modal>
    </Form>
  )
}

export default HostFamilyForm
